import { useState } from 'react';
import { Stack, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from './ConfirmDialog.js';

export interface ListActionsColumnProps<T> {
  row: T;
  onView?: (row: T) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void | Promise<void>;
  canView?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  /** Confirm-Dialog-Title — modul-spezifisch, z.B. "Mitarbeiter loeschen?". Pflicht wenn onDelete gesetzt. */
  deleteTitle?: string;
  /** Confirm-Dialog-Body — modul-spezifisch, z.B. "Diesen Mitarbeiter wirklich loeschen?". Pflicht wenn onDelete gesetzt. */
  deleteMessage?: string;
}

/**
 * Zentrale Actions-Spalte fuer EnhancedDataTable. Permission-bewusst —
 * Buttons werden ausgeblendet wenn `can*` explizit false ist. Delete oeffnet
 * einen ConfirmDialog und ruft erst nach Bestaetigung den Handler.
 */
export function ListActionsColumn<T>({
  row,
  onView,
  onEdit,
  onDelete,
  canView = true,
  canEdit = true,
  canDelete = true,
  deleteTitle,
  deleteMessage,
}: ListActionsColumnProps<T>) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete(row);
      setConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
        {onView && canView && (
          <Tooltip title={t('actions.view')}>
            <IconButton size="small" onClick={() => onView(row)} aria-label={t('actions.view')}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onEdit && canEdit && (
          <Tooltip title={t('actions.edit')}>
            <IconButton size="small" onClick={() => onEdit(row)} aria-label={t('actions.edit')}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onDelete && canDelete && (
          <Tooltip title={t('actions.delete')}>
            <IconButton
              size="small"
              color="error"
              onClick={handleDeleteClick}
              aria-label={t('actions.delete')}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <ConfirmDialog
        open={confirmOpen}
        title={deleteTitle ?? t('actions.delete')}
        message={deleteMessage ?? ''}
        confirmLabel={t('actions.delete')}
        cancelLabel={t('actions.cancel')}
        confirmColor="error"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
        loading={deleting}
      />
    </>
  );
}
